/**
 * سربرگِ «گزارش‌ها» — صفِ رسیدگی به گزارشِ محتوا و کاربر (مایگریشن 0135).
 *
 * ═══ چرا وجود دارد ═══
 *
 * سیاستِ UGCِ گوگل‌پلی دو چیز می‌خواهد، نه یکی: راهی برای گزارش دادن، و
 * راهی برای **رسیدگی** به آن. نیمه‌ی اول در اپ و سایت است (دکمه‌ی «گزارش»
 * روی سیگنال، پیام و پروفایل)؛ نیمه‌ی دوم همین صفحه است.
 *
 * ═══ چرا هر ردیف خودِ محتوا را نشان می‌دهد ═══
 *
 * درسِ سربرگِ «بازخوردها»: گزارشی که فقط می‌گوید «کاربر X یک پیام را
 * گزارش کرد» تصمیم‌گرفتنی نیست. پس کنارِ هر ردیف سه چیزِ دیگر هست —
 * خودِ متن/عنوانِ گزارش‌شده، نامِ کسی که نوشته‌اش، و (برای پیام) سیگنالی که
 * تویش گفته شده. همه‌شان از `admin_content_reports` می‌آیند و نه از
 * خواندنِ مستقیمِ جدول‌ها: ادمین عضوِ آن سیگنال نیست و RLS متنِ پیام را از
 * او پنهان می‌کند.
 *
 * ═══ سه وضعیت و نه دو ═══
 *
 * «رسیدگی شد» و «چیزی نبود» عمداً از هم جدایند: هر دو ردیف را از صف
 * می‌برند، ولی جوابِ «چند گزارش واقعی بوده» فقط با تفکیکشان به دست می‌آید.
 * و هر دو برگشت‌پذیرند — کسی که اشتباهی بست باید بتواند پسش بگیرد.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { opsRepo, type ContentReportItem, type FilterAppeal } from '@/core/data/opsRepo';
import { formatDateTime } from '@/core/lib/dates';
import {
  REPORT_REASON_LABEL, REPORT_STATUS_LABEL, REPORT_TARGET_ICON, REPORT_TARGET_LABEL,
  isReportReason,
  type ReportStatus, type ReportTargetKind,
} from '@/core/lib/contentReport';
import { Hint } from '@/ui/Hint';
import { fmtNum as fmt } from '@/ui/Num';

/** از کجا پر می‌شود — برای وقتی که صف خالی است و آدم می‌خواهد خودش امتحان کند. */
const FORM_HELP = 'کاربر از دکمه‌ی «گزارش» روی یک سیگنال، یک پیامِ چت، یا کارتِ یک '
  + 'کاربر می‌فرستد (هم در اپ، هم در سایت).';

/** مقدارِ ناشناخته‌ی دیتابیس نباید ردیف را ناخوانا کند — همان قاعده‌ی `feedbackKind`. */
const reasonLabel = (r: string): string =>
  (isReportReason(r) ? REPORT_REASON_LABEL[r] : r);

const targetLabel = (k: string): string =>
  REPORT_TARGET_LABEL[k as ReportTargetKind] ?? k;

const targetIcon = (k: string): string =>
  REPORT_TARGET_ICON[k as ReportTargetKind] ?? '•';

const statusLabel = (s: string): string =>
  REPORT_STATUS_LABEL[s as ReportStatus] ?? s;

/* ─────────────────────────── یک ردیف ─────────────────────────── */

function Row({ r, onResolve, onRemove, onEject, busy }: {
  r: ContentReportItem;
  onResolve: (status: ReportStatus, note: string | null) => void;
  /**
   * «حذفِ محتوا» و «اخراجِ کاربر» — WHOSUP-309، مایگریشن 0160. از 0175
   * «حذف» یعنی پنهان از همه جز صاحبش، با دلیلِ اجباری و شرطِ برگشت.
   */
  onRemove: (reason: string, fix: string | null) => void;
  onEject: (userId: string) => void;
  busy: boolean;
}) {
  const [note, setNote] = useState('');
  const [noteOpen, setNoteOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [fix, setFix] = useState('');
  // جلوی نامِ نویسنده، روی هر گزارشی — باز یا بسته، حذف‌شده یا نه
  // (تستِ دستیِ WHOSUP-309-A4: «جلوی اسم کاربر باشه»).
  const ejectBtn = r.author_banned
    ? <span className="chip on">اخراج شده</span>
    : r.author_id ? (
      <button type="button" className="linkbtn" disabled={busy}
        onClick={() => {
          if (window.confirm(`«${r.author}» اخراج شود؟ ورودش برای همیشه بسته، سیگنال‌هایش حذف و از فهرستِ دوستانِ همه بیرون می‌رود.`)) {
            onEject(r.author_id!);
          }
        }}>
        اخراجِ کاربر
      </button>
    ) : null;

  return (
    <div className="dup">
      <div className="dup-head">
        <span className="chip">{targetIcon(r.target_kind)} {targetLabel(r.target_kind)}</span>
        <span className={`chip${r.status === 'open' ? ' warn' : ''}`}>
          {reasonLabel(r.reason)}
        </span>
        <span className="dim">{formatDateTime(r.at)}</span>
        <span className="spacer" style={{ flex: 1 }} />
        {r.status !== 'open' ? (
          <span className="chip on">{statusLabel(r.status)}</span>
        ) : null}
      </div>

      {/* خودِ چیزی که گزارش شده. متنِ خامِ کاربر است — پس متن، نه HTML. */}
      <p style={{ whiteSpace: 'pre-wrap', margin: '6px 0', fontSize: 15 }}>
        {r.removed ? <span className="chip">پنهان شد — فقط صاحبش و ادمین می‌بینند</span> : null} {r.target}
      </p>

      <table className="tbl">
        <tbody>
          <tr><th>نوشته‌ی کی</th><td>{r.author} {ejectBtn}</td></tr>
          {r.context ? <tr><th>در سیگنالِ</th><td>{r.context}</td></tr> : null}
          <tr><th>گزارش‌دهنده</th><td>{r.reporter}</td></tr>
          {r.note ? (
            <tr>
              <th>توضیحش</th>
              <td style={{ whiteSpace: 'pre-wrap' }}>{r.note}</td>
            </tr>
          ) : null}
          {r.reviewed_at ? (
            <tr>
              <th>رسیدگی</th>
              <td>
                {formatDateTime(r.reviewed_at)}
                {r.reviewed_by ? ` · ${r.reviewed_by}` : ''}
                {r.admin_note ? ` · ${r.admin_note}` : ''}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      <div className="chiprow" style={{ marginTop: 6 }}>
        {r.status === 'open' ? (
          <>
            {/* تأییدِ مرورگر عمدی است: هر دو کار روی محتوای یک آدمِ واقعی
                است و دکمه‌ای که با یک کلیکِ اشتباه کسی را بیرون کند، نباید
                بی‌پرسش باشد. */}
            {!r.removed ? (
              <button type="button" className={`btn${removeOpen ? ' on' : ''}`} disabled={busy}
                onClick={() => setRemoveOpen((v) => !v)}>
                حذفِ محتوا
              </button>
            ) : null}
            <button type="button" className="btn" disabled={busy}
              onClick={() => onResolve('actioned', note.trim() || null)}>
              رسیدگی شد
            </button>
            <button type="button" className="btn" disabled={busy}
              onClick={() => onResolve('dismissed', note.trim() || null)}>
              چیزی نبود
            </button>
            {/* یادداشت اختیاری است و پشتِ یک کلیک: در بیشترِ ردیف‌ها چیزی
                برای نوشتن نیست و یک کادرِ همیشه‌باز فقط صف را بلند می‌کند. */}
            <button type="button" className="linkbtn" onClick={() => setNoteOpen((v) => !v)}>
              {noteOpen ? 'بدونِ یادداشت' : 'یادداشت'}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="linkbtn" disabled={busy}
              onClick={() => onResolve('open', null)}>
              برگرداندن به صف
            </button>
          </>
        )}
      </div>

      {/* «حذف» دیگر پاک نمی‌کند: از همه جز صاحبش پنهان می‌شود و صاحبش
          دلیل و شرط را می‌بیند و خبر می‌گیرد (0175). نامِ نمایه استثناست —
          نام برای دیگران است، پس به نامِ کاربری برمی‌گردد. */}
      {r.status === 'open' && removeOpen && !r.removed ? (
        <div style={{ marginTop: 6, display: 'grid', gap: 6 }}>
          <input className="input" value={reason} placeholder="دلیل (اجباری) — صاحبش همین را می‌بیند"
            onChange={(e) => setReason(e.target.value)} />
          <input className="input" value={fix}
            placeholder="در چه صورتی دوباره دیده می‌شود؟ مثلاً «عنوان و توضیح را بدونِ ناسزا بنویس» (اختیاری)"
            onChange={(e) => setFix(e.target.value)} />
          <div className="chiprow">
            <button type="button" className="btn" disabled={busy || !reason.trim()}
              onClick={() => onRemove(reason.trim(), fix.trim() || null)}>
              {r.target_kind === 'profile' ? 'بازنشانیِ نام و خبر به صاحبش' : 'پنهان کن و به صاحبش بگو'}
            </button>
          </div>
        </div>
      ) : null}

      {r.status === 'open' && noteOpen ? (
        <input className="input" value={note} placeholder="چه کردی؟ (اختیاری)"
          style={{ marginTop: 6 }} onChange={(e) => setNote(e.target.value)} />
      ) : null}
    </div>
  );
}

/* ─────────────────────────── خودِ سربرگ ─────────────────────────── */

type Filter = 'open' | 'actioned' | 'dismissed' | 'all';

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'open', label: 'باز' },
  { id: 'actioned', label: 'رسیدگی شد' },
  { id: 'dismissed', label: 'چیزی نبود' },
  { id: 'all', label: 'همه' },
];

export function ReportsTab() {
  // پیش‌فرض «باز» است و نه «همه»: کارِ این صفحه رسیدگی است، و صفی که با
  // ردیف‌های بسته شروع شود، اولین بار که پنجاه‌تایی شد بی‌فایده می‌شود.
  const [filter, setFilter] = useState<Filter>('open');
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['contentReports', filter],
    queryFn: () => opsRepo.contentReports(filter === 'all' ? null : filter),
  });

  const resolve = useMutation({
    mutationFn: (a: { id: number; status: ReportStatus; note: string | null }) =>
      opsRepo.resolveReport(a.id, a.status, a.note),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contentReports'] }),
  });
  const remove = useMutation({
    mutationFn: (a: { id: number; reason: string; fix: string | null }) =>
      opsRepo.removeReportedContent(a.id, a.reason, a.fix),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contentReports'] }),
  });
  const eject = useMutation({
    mutationFn: (userId: string) => opsRepo.ejectUser(userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contentReports'] }),
  });
  const busy = resolve.isPending || remove.isPending || eject.isPending;
  const actErr = resolve.error ?? remove.error ?? eject.error;

  if (q.isLoading) return <p className="dim">…</p>;
  if (q.error) return <div className="card"><p className="err">{String(q.error)}</p></div>;

  const d = q.data!;
  const count = (k: Filter) => (k === 'all' ? d.total
    : k === 'open' ? d.open
      : k === 'actioned' ? d.actioned : d.dismissed);

  return (
    <>
      <div className="grid n3" style={{ marginTop: 12 }}>
        <div className="card stat">
          <div className="n">{fmt(d.open)}</div>
          <div className="k"><Hint text="منتظرِ رسیدگی" help={FORM_HELP} /></div>
        </div>
        <div className="card stat">
          <div className="n">{fmt(d.actioned)}</div>
          <div className="k">رسیدگی شد</div>
        </div>
        <div className="card stat">
          <div className="n">{fmt(d.dismissed)}</div>
          <div className="k">
            <Hint text="چیزی نبود"
              help="گزارشی که بررسی شد و ایرادی نداشت — از «رسیدگی شد» جدا می‌ماند تا معلوم بماند چند گزارش واقعی بوده" />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="chiprow">
          {FILTERS.map((f) => (
            <button key={f.id} type="button" className={`chip${filter === f.id ? ' on' : ''}`}
              onClick={() => setFilter(f.id)}>
              {f.label} {fmt(count(f.id))}
            </button>
          ))}
        </div>

        {actErr ? <p className="err">{String(actErr)}</p> : null}

        {d.total === 0 ? (
          // خالی بودن هم یک یافته است، نه صفحه‌ی خراب — پس می‌گوید از کجا پر
          // می‌شود. (همان قاعده‌ی سربرگِ بازخوردها.)
          <p className="dim" style={{ marginTop: 10 }}>
            هنوز هیچ گزارشی نیامده. {FORM_HELP} هر گزارشِ تازه همان لحظه به تلگرام و ایمیل هم می‌رود.
          </p>
        ) : d.items.length === 0 ? (
          <p className="dim" style={{ marginTop: 10 }}>در این وضعیت چیزی نیست.</p>
        ) : (
          <div style={{ marginTop: 6 }}>
            {d.items.map((r) => (
              <Row key={r.id} r={r} busy={busy}
                onResolve={(status, note) => resolve.mutate({ id: r.id, status, note })}
                onRemove={(reason, fix) => remove.mutate({ id: r.id, reason, fix })}
                onEject={(uid) => eject.mutate(uid)} />
            ))}
          </div>
        )}
      </div>

      <AppealsCard />
    </>
  );
}

/* ─────────────────────── اعتراض به فیلترِ واژه‌ها ─────────────────────── */

const FIELD_LABEL: Record<FilterAppeal['field'], string> = {
  signal: 'سیگنال', message: 'پیام', profile_name: 'نامِ نمایه',
};

/**
 * فیلترِ واژه‌ها (`packages/core/src/lib/contentFilter.ts`) جلوی متن را
 * می‌گیرد و کاربر می‌تواند اعتراض کند (WHOSUP-309). «اشتباه گرفت» یعنی
 * آن واژه برای **همان کاربر** آزاد می‌شود (`assertCleanForMe` در
 * `reportRepo`) و او می‌تواند متنش را دوباره بفرستد. بیرون بردنِ واژه از
 * فهرست برای همه یک تغییرِ کد است، نه یک کلیک.
 */
function AppealsCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['filterAppeals'], queryFn: () => opsRepo.filterAppeals('open') });
  const set = useMutation({
    mutationFn: (a: { id: number; status: 'allowed' | 'upheld' }) =>
      opsRepo.resolveFilterAppeal(a.id, a.status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['filterAppeals'] }),
  });
  const items = q.data ?? [];
  return (
    <div className="card">
      <h3>
        <Hint text={`اعتراض به فیلترِ واژه‌ها · ${fmt(items.length)}`}
          help="کاربری که متنش با فهرستِ واژه‌های ناپسند گیر کرد و گفت «اشتباه گرفت». دکمه‌ی «اشتباه گرفت» آن واژه را برای همان کاربر آزاد می‌کند تا متنش را دوباره بفرستد، و اگر «سیگنالِ نگه‌داشته» دارد، همان سیگنال همین حالا برای دعوت‌شده‌ها فرستاده می‌شود. «درست گرفت» سیگنال را نگه‌داشته می‌گذارد و صاحبش می‌بیند که رد شد. برای آزاد کردنش برای همه، واژه را در contentFilter.ts عوض کن." />
      </h3>
      {q.error ? <p className="err">{String(q.error)}</p> : null}
      {set.error ? <p className="err">{String(set.error)}</p> : null}
      {items.length === 0 ? <p className="dim">اعتراضِ بازی نیست.</p> : items.map((a) => (
        <div key={a.id} className="dup">
          <div className="dup-head">
            <span className="chip">{FIELD_LABEL[a.field] ?? a.field}</span>
            {a.matched.length ? <span className="chip warn">{a.matched.join('، ')}</span> : null}
            {a.signal_id ? <span className="chip">سیگنالِ نگه‌داشته</span> : null}
            {a.message_id ? <span className="chip">پیامِ پنهان‌شده</span> : null}
            <span className="dim">{formatDateTime(a.at)} · {a.user}</span>
          </div>
          {/* نگه‌داشتنِ مدیر (0175): دلیل و شرطی که خودت گذاشته بودی. */}
          {a.held_reason ? (
            <p className="dim" style={{ margin: '6px 0' }}>
              دلیلِ پنهان کردن: {a.held_reason}{a.held_fix ? ` · شرطِ برگشت: ${a.held_fix}` : ''}
            </p>
          ) : null}
          <p style={{ whiteSpace: 'pre-wrap', margin: '6px 0' }}>{a.body}</p>
          <div className="chiprow">
            <button type="button" className="btn" disabled={set.isPending}
              onClick={() => set.mutate({ id: a.id, status: 'allowed' })}>
              {a.held_reason ? 'درست شد — دوباره نشان بده' : 'اشتباه گرفت'}
            </button>
            <button type="button" className="btn" disabled={set.isPending}
              onClick={() => set.mutate({ id: a.id, status: 'upheld' })}>
              {a.held_reason ? 'هنوز نه' : 'درست گرفت'}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
